<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Additions and deductions, costs subject to VAT"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasAdditionsDeductionsData"
            :has-errors="hasSectionErrors"
          />
          Additions and deductions, costs subject to VAT
        </span>
      </template>
      <!-- Method of dividing additions and deductions field -->
      <b-row v-if="parcel.costs_distribution_data">
        <b-col
          cols="12"
          md="3"
        >
          <validation-provider
            v-slot="{ errors }"
            name="Method of dividing additions and deductions"
          >
            <b-form-group>
              <label>
                Method of dividing additions and deductions
                <small class="text-muted">140400000C</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Method used to divide additions and deductions across items'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <v-select
                v-model="parcel.costs_distribution_data.costs_distribution_method"
                :options="methodOptions"
                :reduce="option => option.value"
                :disabled="disabled"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <!-- Transport cost as AK row (bound to transport_cost / transport_cost_currency) -->
      <div
        v-if="showTransportAkRow"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group>
              <label>
                Code <span class="text-danger">*</span>
                <small class="text-muted">1404008000</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Transport cost (AK) — stored in transport cost fields'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <v-select
                :value="TRANSPORT_AK_CODE"
                :options="[{ label: TRANSPORT_AK_CODE, value: TRANSPORT_AK_CODE }]"
                :reduce="option => option.value"
                :clearable="false"
                disabled
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport cost amount"
              :rules="transportAkRowHasData ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Amount <span class="text-danger">*</span>
                  <small class="text-muted">1404014000</small>
                </label>
                <b-form-input
                  v-model="parcel.transport_cost"
                  v-b-tooltip.hover.top="getIssueMessage('transport_cost')"
                  :class="getIssueClass('transport_cost')"
                  :disabled="disabled"
                  type="number"
                  step="0.01"
                  :state="errors.length > 0 || getIssueClass('transport_cost') ? false : null"
                />
                <small class="text-danger">{{ errors[0] || getIssueMessage('transport_cost') }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport cost currency"
              :rules="transportAkRowHasData ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Currency <span class="text-danger">*</span>
                  <small class="text-muted">140400000B</small>
                </label>
                <v-select
                  v-model="parcel.transport_cost_currency"
                  v-b-tooltip.hover.top="getIssueMessage('transport_cost_currency')"
                  :class="getIssueClass('transport_cost_currency')"
                  :options="currencyOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 || getIssueClass('transport_cost_currency') ? false : null"
                />
                <small class="text-danger">{{ errors[0] || getIssueMessage('transport_cost_currency') }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
          />
        </b-row>
      </div>

      <div
        v-for="(deduction, deductionIndex) in nonAkAdditions"
        :key="deduction._uiKey || deductionIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Code"
              :rules="rowHasData(deduction) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Code <span class="text-danger">*</span>
                  <small class="text-muted">1404008000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Code for addition or deduction'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="deduction.code"
                  :options="selectableCodeOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                  @input="onDeductionCodeChange(deduction, $event)"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Amount"
              :rules="rowHasData(deduction) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Amount <span class="text-danger">*</span>
                  <small class="text-muted">1404014000</small>
                </label>
                <b-form-input
                  v-model="deduction.amount"
                  v-b-tooltip.hover.top="getIssueMessage(additionIssuePath(deduction, 'amount'))"
                  :class="getIssueClass(additionIssuePath(deduction, 'amount'))"
                  :disabled="disabled"
                  type="number"
                  step="0.01"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Currency"
              :rules="rowHasData(deduction) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Currency <span class="text-danger">*</span>
                  <small class="text-muted">140400000B</small>
                </label>
                <v-select
                  v-model="deduction.currency"
                  :options="currencyOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeDeduction(deduction)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addDeduction"
          >
            <feather-icon icon="PlusIcon" />
            New row
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

const TRANSPORT_AK_CODE = 'AK'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      TRANSPORT_AK_CODE,
      codeOptions: [
        { label: 'AK — Transport cost', value: 'AK' },
        { label: 'AL', value: 'AL' },
        { label: 'test', value: '1' },
      ],
      currencyOptions: [
        'EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RUB', 'TRY', 'INR', 'BRL', 'ZAR', 'KRW', 'MXN', 'IDR', 'MYR', 'PHP', 'THB', 'SGD', 'NZD', 'HKD', 'AED', 'SAR', 'ILS', 'EGP',
      ],
      methodOptions: [
        { label: 'Proportional to value', value: 1 },
        { label: 'Proportional to quantity', value: 2 },
        { label: 'Proportional to weight', value: 3 },
        { label: 'Equal distribution', value: 4 },
      ],
    }
  },
  computed: {
    selectableCodeOptions() {
      // AK is not selectable on regular rows — use dedicated transport cost row
      return this.codeOptions.filter(option => option.value !== TRANSPORT_AK_CODE)
    },
    additionsList() {
      if (!this.parcel.costs_distribution_data || !Array.isArray(this.parcel.costs_distribution_data.additions_deductions)) {
        return []
      }
      return this.parcel.costs_distribution_data.additions_deductions
    },
    nonAkAdditions() {
      return this.additionsList.filter(entry => !this.isAkCode(entry && entry.code))
    },
    hasTransportCostValues() {
      const amount = this.parcel && this.parcel.transport_cost
      const currency = this.parcel && this.parcel.transport_cost_currency
      return (amount !== null && amount !== undefined && amount !== '') || !!(currency)
    },
    showTransportAkRow() {
      return true
    },
    transportAkRowHasData() {
      return this.hasTransportCostValues
    },
    hasAdditionsDeductionsData() {
      const hasMethodData = this.parcel && this.parcel.costs_distribution_data && this.parcel.costs_distribution_data.costs_distribution_method
      const hasDeductionsData = this.nonAkAdditions.some(deduction => deduction.code || deduction.amount || deduction.currency)
      return !!(hasMethodData || hasDeductionsData || this.hasTransportCostValues)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => (
        key.startsWith('costs_distribution_data.')
        || key === 'transport_cost'
        || key === 'transport_cost_currency'
      ))
    },
  },
  watch: {
    parcel: {
      immediate: true,
      handler() {
        this.normalizeAkFromAdditions()
      },
    },
    'parcel.costs_distribution_data.additions_deductions': {
      deep: true,
      handler() {
        this.normalizeAkFromAdditions()
      },
    },
  },
  methods: {
    isAkCode(code) {
      if (code == null || code === '') {
        return false
      }
      const value = typeof code === 'object' && code !== null && 'value' in code ? code.value : code
      return String(value).toUpperCase() === TRANSPORT_AK_CODE
    },
    ensureCostsDistributionData() {
      if (!this.parcel.costs_distribution_data) {
        this.$set(this.parcel, 'costs_distribution_data', { costs_distribution_method: null, additions_deductions: [] })
      } else if (!this.parcel.costs_distribution_data.additions_deductions) {
        this.$set(this.parcel.costs_distribution_data, 'additions_deductions', [])
      }
    },
    /**
     * Move any AK entries out of additions_deductions into transport_cost fields.
     * transport_cost / transport_cost_currency remain the single source of truth.
     */
    normalizeAkFromAdditions() {
      if (!this.parcel || !this.parcel.costs_distribution_data || !Array.isArray(this.parcel.costs_distribution_data.additions_deductions)) {
        return
      }
      const list = this.parcel.costs_distribution_data.additions_deductions
      const akEntries = list.filter(entry => this.isAkCode(entry && entry.code))
      if (akEntries.length === 0) {
        return
      }
      const firstAk = akEntries[0]
      const hasTransportAmount = this.parcel.transport_cost !== null && this.parcel.transport_cost !== undefined && this.parcel.transport_cost !== ''
      const hasTransportCurrency = !!(this.parcel.transport_cost_currency)
      if (!hasTransportAmount && firstAk.amount !== null && firstAk.amount !== undefined && firstAk.amount !== '') {
        this.$set(this.parcel, 'transport_cost', firstAk.amount)
      }
      if (!hasTransportCurrency && firstAk.currency) {
        const currency = typeof firstAk.currency === 'object' && 'value' in firstAk.currency
          ? firstAk.currency.value
          : firstAk.currency
        this.$set(this.parcel, 'transport_cost_currency', currency)
      }
      this.parcel.costs_distribution_data.additions_deductions = list.filter(entry => !this.isAkCode(entry && entry.code))
    },
    additionIssuePath(deduction, field) {
      const index = this.additionsList.indexOf(deduction)
      if (index < 0) {
        return `costs_distribution_data.additions_deductions.0.${field}`
      }
      return `costs_distribution_data.additions_deductions.${index}.${field}`
    },
    addDeduction() {
      this.ensureCostsDistributionData()
      this.parcel.costs_distribution_data.additions_deductions.push({
        code: null,
        amount: '',
        currency: null,
      })
    },
    removeDeduction(deduction) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this row?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          const index = this.additionsList.indexOf(deduction)
          if (index >= 0) {
            this.parcel.costs_distribution_data.additions_deductions.splice(index, 1)
          }
        }
      })
    },
    onDeductionCodeChange(deduction, code) {
      if (!this.isAkCode(code)) {
        return
      }
      // User somehow selected AK: bind to transport fields and remove from list
      const hasTransportAmount = this.parcel.transport_cost !== null && this.parcel.transport_cost !== undefined && this.parcel.transport_cost !== ''
      if (!hasTransportAmount && deduction.amount !== null && deduction.amount !== undefined && deduction.amount !== '') {
        this.$set(this.parcel, 'transport_cost', deduction.amount)
      }
      if (!this.parcel.transport_cost_currency && deduction.currency) {
        this.$set(this.parcel, 'transport_cost_currency', deduction.currency)
      }
      const index = this.additionsList.indexOf(deduction)
      if (index >= 0) {
        this.parcel.costs_distribution_data.additions_deductions.splice(index, 1)
      }
    },
    rowHasData(deduction) {
      return !!(deduction.code || deduction.amount || deduction.currency)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
